/**
 * GRNs — Goods Received Notes list (Procurement / P3-7).
 * Institutional UI, Hybrid Elite design, UAE-compliant numbering on display.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Package, Plus, Search, Filter, ChevronRight,
  CheckCircle2, Clock, AlertTriangle, XCircle,
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent } from '../../../../components/ui/card';
import BackButton from '../../../../components/admin/BackButton';
import { useToast } from '../../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: Clock },
  partial: { label: 'Partial', color: 'bg-amber-100 text-amber-800', icon: AlertTriangle },
  received: { label: 'Received', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const GRNs = () => {
  const [grns, setGrns] = useState([]);
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', po_id: '', supplier_id: '', q: '' });
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.po_id) params.append('po_id', filters.po_id);
      if (filters.supplier_id) params.append('supplier_id', filters.supplier_id);
      const [g, p, s] = await Promise.all([
        axios.get(`${API_URL}/api/procurement/grns?${params}`, auth),
        axios.get(`${API_URL}/api/procurement/purchase-orders`, auth),
        axios.get(`${API_URL}/api/procurement/suppliers`, auth),
      ]);
      setGrns(g.data || []);
      setPos(p.data || []);
      setSuppliers(s.data || []);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to load GRNs.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filters.status, filters.po_id, filters.supplier_id]); // eslint-disable-line

  const filtered = useMemo(() => {
    const q = filters.q.toLowerCase().trim();
    if (!q) return grns;
    return grns.filter((g) =>
      [g.grn_number, g.po_snapshot?.po_number, g.supplier_snapshot?.company_name, g.notes]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(q))
    );
  }, [grns, filters.q]);

  const kpis = useMemo(() => {
    const counts = { total: grns.length, draft: 0, partial: 0, received: 0, rejected: 0 };
    for (const g of grns) counts[g.status] = (counts[g.status] || 0) + 1;
    return counts;
  }, [grns]);

  return (
    <div className="p-6 space-y-5" data-testid="grns-page">
      <BackButton />

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#2B2D42] flex items-center gap-2">
            <Package className="w-6 h-6 text-[#023E8A]" /> Goods Received Notes (GRN)
          </h1>
          <p className="text-sm text-[#6C757D] mt-1 max-w-2xl">
            Record deliveries against a Purchase Order. Posting a GRN is irreversible — it
            updates stock, PO delivery status, and writes an immutable audit trail.
          </p>
        </div>
        <Button
          onClick={() => navigate('/admin/procurement/grns/new')}
          className="bg-[#FF6B35] hover:bg-[#E5562D] text-white"
          data-testid="grn-new-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> New GRN
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="grn-kpis">
        <KPI label="Total GRNs" value={kpis.total} color="text-[#023E8A]" />
        <KPI label="Draft" value={kpis.draft} dotColor="bg-gray-400" />
        <KPI label="Partial" value={kpis.partial} dotColor="bg-amber-500" />
        <KPI label="Received" value={kpis.received} dotColor="bg-green-600" />
        <KPI label="Rejected" value={kpis.rejected} dotColor="bg-red-500" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
            placeholder="Search by GRN number, PO, supplier, notes…"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            data-testid="grn-search"
          />
        </div>
        <select
          className="px-3 py-2 border rounded-lg text-sm bg-white"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          data-testid="grn-status-filter"
        >
          <option value="">All statuses</option>
          {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <select
          className="px-3 py-2 border rounded-lg text-sm bg-white"
          value={filters.po_id}
          onChange={(e) => setFilters({ ...filters, po_id: e.target.value })}
          data-testid="grn-po-filter"
        >
          <option value="">All POs</option>
          {pos.map((p) => <option key={p.id} value={p.id}>{p.po_number}</option>)}
        </select>
        <select
          className="px-3 py-2 border rounded-lg text-sm bg-white"
          value={filters.supplier_id}
          onChange={(e) => setFilters({ ...filters, supplier_id: e.target.value })}
          data-testid="grn-supplier-filter"
        >
          <option value="">All suppliers</option>
          {suppliers.map((s) => <option key={s.id} value={s.id}>{s.company_name}</option>)}
        </select>
      </div>

      {/* List */}
      <Card>
        <CardContent className="p-0 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-[#6C757D]">Loading GRNs…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-[#6C757D] mb-3">No Goods Received Notes yet.</p>
              <Button onClick={() => navigate('/admin/procurement/grns/new')} className="bg-[#00B4D8] text-white" data-testid="grn-empty-new-btn">
                <Plus className="w-4 h-4 mr-2" /> Create your first GRN
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {filtered.map((g) => {
                const meta = STATUS_META[g.status] || STATUS_META.draft;
                const Icon = meta.icon;
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => navigate(`/admin/procurement/grns/${g.id}`)}
                    className="w-full flex items-center justify-between gap-3 px-5 py-3 hover:bg-gray-50 transition text-left"
                    data-testid={`grn-row-${g.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <code className="text-xs bg-[#023E8A]/10 text-[#023E8A] px-2 py-0.5 rounded font-mono">
                          {g.grn_number}
                        </code>
                        <span className={`text-xs px-2 py-0.5 rounded inline-flex items-center gap-1 ${meta.color}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-[#2B2D42]">
                        {g.supplier_snapshot?.company_name || 'Unknown supplier'}
                        <span className="text-xs font-normal text-[#6C757D] ml-2">
                          · PO {g.po_snapshot?.po_number || '—'}
                        </span>
                      </div>
                      <div className="text-xs text-[#6C757D]">
                        {g.items.length} line(s) · Received {g.received_date?.slice(0, 10)}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const KPI = ({ label, value, color = 'text-[#2B2D42]', dotColor }) => (
  <div className="bg-white border rounded-lg px-3 py-2">
    <div className="flex items-center gap-2 text-[10px] text-[#6C757D] uppercase tracking-wide">
      {dotColor && <span className={`inline-block w-2 h-2 rounded-full ${dotColor}`} />}
      {label}
    </div>
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
  </div>
);

export default GRNs;
